import React from 'react'
// libs
import styled, { css } from 'styled-components'
// components
import MoreVertRounded from '@mui/icons-material/MoreVertRounded'
// themes
import { colors } from 'themes'
import {
  Button,
  EditIcon,
  TrashIcon,
  ButtonsWrapper
} from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property/view'
// utils
import { transparentize } from 'polished'
import { isDescendant } from 'utils/plan-tree'
// context
import { ScaleModalTreeNodeRendererContext } from './TreeNodeRenderer'

// styled
const DragIcon = styled(MoreVertRounded)`
  color: ${colors.gray900};
  cursor: move;
`

const ContentInnerWrapper = styled.div`
  position: relative;
  width: 100%;
`

const IconsWrapper = styled.div`
  align-items: center;
  display: flex;
`

const NodeContent = styled.div`
  align-items: center;
  color: ${({ isActive }) => (isActive ? 'unset' : colors.gray350)};
  display: flex;
  width: 100%;
`

const Wrapper = styled.div`
  background: ${colors.gray200};
  border-radius: 5px;
  border: ${({ isHovered }) => (isHovered ? `2px solid ${colors.blue500}` : 'unset')};
  display: flex;
  height: 100%;
  margin: 0 10px;
  padding: 5px 15px;

  ${({ isLandingPad }) =>
    isLandingPad &&
    css`
      background: ${transparentize(0.7, colors.purple300)};
      border: 3px dotted ${colors.purple300};
      & > * {
        visibility: hidden;
      }
    `};
  ${({ isCancelPad }) =>
    isCancelPad &&
    css`
      background: ${transparentize(0.7, colors.red300)};
    `};

  ${ButtonsWrapper} {
    top: 0;
    right: 20px;
  }
`

const ContentWrapper = styled.div`
  align-items: center;
  cursor: ${({ canDrag }) => (canDrag ? 'cursor: move' : 'cursor: not-allowed;')};
  display: flex;
  padding-left: 10px;
  width: 100%;
`

const MinimalThemeNodeContentRenderer = ({
  buttons,
  canDrag,
  canDrop,
  connectDragPreview,
  connectDragSource,
  didDrop,
  draggedNode,
  icons,
  isDragging,
  isOver, // Not needed, but preserved for other renderers
  isSearchFocus,
  isSearchMatch,
  node,
  parentNode, // Needed for dndManager
  path,
  rowDirection,
  scaffoldBlockPxWidth,
  setTree,
  style,
  subtitle,
  swapDepth,
  swapFrom,
  swapLength,
  title,
  toggleChildrenVisibility,
  tree,
  treeId, // Not needed, but preserved for other renderers,
  treeIndex,
  ...otherProps
}) => {
  // state
  const [isHovered, setHovered] = React.useState(false)

  // context
  const { isDragStarted } = React.useContext(ScaleModalTreeNodeRendererContext)

  // constants
  const nodeTitle = title || node.title

  const isBeingDragged = draggedNode && isDescendant(draggedNode, node)
  const isLandingPadActive = !didDrop && isDragging

  // methods
  const connectDrag = (ref) => {
    if (canDrag) {
      connectDragSource(ref, { dropEffect: 'copy' })
      connectDragPreview(ref)
    }
  }

  const editNode = () => node.handleEditClick()

  const deleteNode = () => node.handleDeleteClick({ node })

  const onMouseEnter = () => {
    if (isDragStarted) return
    setHovered(true)
  }

  const onMouseLeave = () => setHovered(false)

  const onDragStart = () => setHovered(false)

  return (
    <Wrapper
      isBeingDragged={isBeingDragged}
      isCancelPad={isLandingPadActive && !canDrop}
      isHovered={isHovered}
      isLandingPad={isLandingPadActive}
      onDragStart={onDragStart}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={connectDrag}
      {...otherProps}>
      <IconsWrapper>
        <DragIcon />
      </IconsWrapper>

      <ContentWrapper canDrag={canDrag}>
        <ContentInnerWrapper style={style}>
          <NodeContent isActive={node.active}>{nodeTitle}</NodeContent>
        </ContentInnerWrapper>
      </ContentWrapper>

      {isHovered && (
        <ButtonsWrapper>
          <Button onClick={editNode}>
            <EditIcon />
          </Button>
          <Button onClick={deleteNode}>
            <TrashIcon />
          </Button>
        </ButtonsWrapper>
      )}
    </Wrapper>
  )
}

MinimalThemeNodeContentRenderer.defaultProps = {
  buttons: [],
  canDrag: false,
  canDrop: false,
  className: '',
  draggedNode: null,
  icons: [],
  isSearchFocus: false,
  isSearchMatch: false,
  parentNode: null,
  rowDirection: 'ltr',
  style: {},
  subtitle: null,
  swapDepth: null,
  swapFrom: null,
  swapLength: null,
  title: null,
  toggleChildrenVisibility: null
}

// MinimalThemeNodeContentRenderer.propTypes = {
//   buttons: arrayOf(node),
//   canDrag: bool,
//   className: string,
//   icons: arrayOf(node),
//   isSearchFocus: bool,
//   isSearchMatch: bool,
//   node: shape({}).isRequired,
//   path: arrayOf(oneOfType([string, number])).isRequired,
//   scaffoldBlockPxWidth: number.isRequired,
//   style: shape({}),
//   subtitle: oneOfType([func, node]),
//   swapDepth: number,
//   swapFrom: number,
//   swapLength: number,
//   title: oneOfType([func, node]),
//   toggleChildrenVisibility: func,
//   treeIndex: number.isRequired,
//   treeId: string.isRequired,
//
//   // Drag and drop API functions
//   // Drag source
//   connectDragPreview: func.isRequired,
//   connectDragSource: func.isRequired,
//   didDrop: bool.isRequired,
//   draggedNode: shape({}),
//   isDragging: bool.isRequired,
//   parentNode: shape({}), // Needed for dndManager
//
//   // Drop target
//   canDrop: bool,
//   isOver: bool.isRequired,
//   rowDirection: string
// }

export default MinimalThemeNodeContentRenderer
